<!-- src/components/course/CertificatesManager.vue -->
<template>
  <div class="certificates-manager">
    <!-- Режим: Список сертификатов -->
    <template v-if="currentView === 'list'">
      <CertificatesList
        :certificates="certificates"
        :loading="loading"
        @create-certificate="openDesigner('create')"
        @create-certificate-text="openTextForm()"
        @edit-certificate="openDesigner('edit', $event)"
        @delete-certificate="handleDelete"
        @preview-certificate="openDesigner('preview', $event)"
      />
      
      <!-- Модальное окно текстовой формы -->
      <div
        v-if="showTextForm"
        class="certificates-manager__modal-overlay"
        @click.self="closeTextForm"
      >
        <CertificateForm
          :certificate="selectedCertificate"
          :saving="saving"
          @submit="handleTextFormSubmit"
          @cancel="closeTextForm"
        />
      </div>
    </template>

    <!-- Режим: Визуальный дизайнер (React) -->
    <CertificateDesignerWrapper
      v-else-if="currentView === 'designer'"
      :initialTemplate="selectedTemplate"
      :courseId="courseId"
      :courseName="courseName"
      :certificateName="selectedCertificate?.template_name || ''"
      :mode="designerMode"
      @save="handleDesignerSave"
      @cancel="closeDesigner"
    />
  </div>
</template>

<script>
import CertificatesList from './CertificatesList.vue'
import CertificateForm from './CertificateForm.vue'
import CertificateDesignerWrapper from './CertificateDesignerWrapper.vue'

export default {
  name: 'CertificatesManager',
  
  components: {
    CertificatesList,
    CertificateForm,
    CertificateDesignerWrapper
  },
  
  props: {
    courseId: {
      type: [Number, String],
      required: true
    },
    courseName: {
      type: String,
      default: ''
    },
    certificates: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  
  data() {
    return {
      currentView: 'list',
      designerMode: 'create',
      selectedCertificate: null,
      selectedTemplate: null,
      showTextForm: false,
      saving: false
    }
  },
  
  mounted() {
    this.restoreDesignerState()
  },

  methods: {
    getDesignerStorageKey() {
      return `certificates.designer.${this.courseId}`
    },

    persistDesignerState() {
      try {
        const payload = {
          view: 'designer',
          mode: this.designerMode,
          selectedTemplate: this.selectedTemplate || null,
          selectedCertificate: this.selectedCertificate || null
        }
        localStorage.setItem(this.getDesignerStorageKey(), JSON.stringify(payload))
      } catch (error) {
        console.warn('Failed to persist designer state:', error)
      }
    },

    restoreDesignerState() {
      try {
        const raw = localStorage.getItem(this.getDesignerStorageKey())
        if (!raw) return
        const saved = JSON.parse(raw)
        if (saved?.view !== 'designer') return

        this.designerMode = saved.mode || 'create'
        this.selectedTemplate = saved.selectedTemplate || null
        this.selectedCertificate = saved.selectedCertificate || null
        this.currentView = 'designer'
      } catch (error) {
        console.warn('Failed to restore designer state:', error)
      }
    },

    clearDesignerState() {
      try {
        localStorage.removeItem(this.getDesignerStorageKey())
      } catch (error) {
        console.warn('Failed to clear designer state:', error)
      }
    },

    openDesigner(mode, certificate = null) {
      this.designerMode = mode
      this.selectedCertificate = certificate
      
      if (certificate?.template_data) {
        try {
          const templateData = typeof certificate.template_data === 'string'
            ? JSON.parse(certificate.template_data)
            : certificate.template_data
          
          if (templateData.content) {
            this.selectedTemplate = typeof templateData.content === 'string'
              ? JSON.parse(templateData.content)
              : templateData.content
          } else if (templateData.layers) {
            this.selectedTemplate = templateData
          } else {
            this.selectedTemplate = null
          }
        } catch (e) {
          console.error('Error parsing template:', e)
          this.selectedTemplate = null
        }
      } else {
        this.selectedTemplate = null
      }
      
      this.currentView = 'designer'
      this.persistDesignerState()
    },
    
    closeDesigner() {
      this.currentView = 'list'
      this.selectedCertificate = null
      this.selectedTemplate = null
      this.designerMode = 'create'
      this.clearDesignerState()
    },
    
    async handleDesignerSave(templateData) {
      this.saving = true
      
      try {
        const payload = {
          course_id: this.courseId,
          template_name: templateData.template_name || `Сертификат - ${this.courseName}`,
          template_description: templateData.template_description || '',
          template_data: templateData.template_content,
          preview_image: templateData.preview_image,
          criteria: Array.isArray(templateData.criteria) 
            ? JSON.stringify(templateData.criteria)
            : templateData.criteria || JSON.stringify(['Завершить курс'])
        }
        
        if (this.selectedCertificate?.id) {
          this.$emit('update-certificate', {
            id: this.selectedCertificate.id,
            ...payload
          })
        } else {
          this.$emit('create-certificate', payload)
        }
        
        this.closeDesigner()
      } catch (error) {
        console.error('Error saving certificate:', error)
        this.$emit('error', 'Ошибка сохранения сертификата')
      } finally {
        this.saving = false
      }
    },
    
    openTextForm(certificate = null) {
      this.selectedCertificate = certificate
      this.showTextForm = true
    },
    
    closeTextForm() {
      this.showTextForm = false
      this.selectedCertificate = null
    },
    
    async handleTextFormSubmit(formData) {
      this.saving = true
      
      try {
        const payload = {
          course_id: this.courseId,
          template_name: formData.template_name,
          template_content: formData.template_content,
          criteria: JSON.stringify(formData.criteria)
        }
        
        if (this.selectedCertificate?.id) {
          this.$emit('update-certificate', {
            id: this.selectedCertificate.id,
            ...payload
          })
        } else {
          this.$emit('create-certificate', payload)
        }
        
        this.closeTextForm()
      } catch (error) {
        console.error('Error saving certificate:', error)
        this.$emit('error', 'Ошибка сохранения')
      } finally {
        this.saving = false
      }
    },
    
    handleDelete(certificate) {
      this.$emit('delete-certificate', certificate)
    }
  }
}
</script>

<style scoped>
.certificates-manager {
  width: 100%;
}

.certificates-manager__modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}
</style>
